import { ICommand } from 'core-ts-types';
import { IQuery } from 'core-ts-types';
import * as _4 from 'lodash4';
import { CommunicatorService } from 'ui-ng-component-communicator';
import { RouterHelper } from '../../../global/helpers/router_helper';
import { APDegreeMapQueryHelper } from '../../../plan/queries/ap_degree_map_query_helper';
import { CourseSlot } from '../../builders/ap_generic_slot_builder';
import { APComponentStateModel } from '../../models/ap_component_state_model';
import { APGenericSlotServant } from '../../servants/ap_generic_slot_servant';
import { APMinGradeServant } from '../../servants/ap_min_grade_servant';
import { IAPAPMarkAsMetStatusDatasource } from '../mark_as_met/datasources/mark_as_met_status_datasource';
import { MarkAsMetFacade } from '../mark_as_met/facades/mark_as_met_facade';
import { IApplicationConfiguration } from './../../../global/commands/initialize_user_activity_tracking_command';
import { APUserModeConfig } from './../../../global/components/user_mode_config/config/ap_user_mode_config';
import { APProgramSlotHelper } from './../../../global/helpers/program_slot_helper';
import { ICourseDetailState } from './../../../routes/ap_course_details_routes';

interface ITermEntry {
  id: number;
  name: string;
}

class CourseTileMenuController {
  private genericSlot: CourseSlot;
  private isAdvisor: boolean;
  private isPlanned: boolean = false;
  private slot: number;
  private showMenu: boolean = true;
  private hasMetRequirements: boolean = true;
  private courseLock: boolean = false;
  private showLockCourseOption: boolean;
  private showUnlockCourseOption: boolean;
  private showAddToTermMenu: boolean = false;
  private showFlagCourseMenu: boolean = false;
  private showRemoveFromTermMenu: boolean = false;
  private popupMenuId: string;
  private openLockDialog: boolean = false;
  private lockDialogMode: string = 'lock';
  private addToTermMenuItemText = 'Add Course to Term';
  private isSlotInPlannedStatus = false;
  private isOpened = false;
  private shouldShowAddToTermMenu = false;

  constructor(
    private removeCourseFromTermCommand: ICommand,
    private saveCourseToTermCommand: ICommand,
    private removeCourseFromTermAndAddCourseCommand: ICommand,
    private ngAPTermsQuery: IQuery,
    private ngAPUserModeConfig: APUserModeConfig,
    private ngComponentCommunicatorService: CommunicatorService,
    private ngAPComponentStateModel: APComponentStateModel,
    private routerHelper: RouterHelper,
    private ngAPMarkAsMetStatusDatasource: IAPAPMarkAsMetStatusDatasource,
    private markAsMetFacade: MarkAsMetFacade,
    private phxAxTitleAnnouncerDelegate: any,
    private ngAPProgramSlotHelper: APProgramSlotHelper,
    private ngAPDegreeMapQueryHelper: APDegreeMapQueryHelper,
    private appConfigModel: IApplicationConfiguration
  ) {
    this.popupMenuId = _4.uniqueId();
  }

  isMovePreventedByMarkAsMet() {
    const programSlot = this.genericSlot.programSlot;
    if (!this.ngAPDegreeMapQueryHelper.isMarkAsMetFeatureEnabled() || _4.isEmpty(programSlot)) {
      return false;
    }

    return this.ngAPProgramSlotHelper.isMarkedAsMet(this.genericSlot.programSlot);
  }

  menuItemText() {
    const programSlot = this.genericSlot.programSlot;
    const plannedCourse = this.genericSlot.plannedCourse;
    const isInPastTerm = this.isCourseCompleted && this.genericSlot.isLockedByStatus;
    const programSlotStatusEmpty = APMinGradeServant.progSlotEmptyPlannedCourseCompleted(programSlot, plannedCourse);
    if (programSlotStatusEmpty) {
      return 'Add Course to Term';
    }
    return this.isPlanned && !(isInPastTerm && !this.isMinGradeMet) ? 'Move to Term' : 'Add Course to Term';
  }

  initialize() {
    this.isAdvisor = this.ngAPUserModeConfig.isAdvisor();
    this.isPlanned = APGenericSlotServant.isGenericSlotPlanned(this.genericSlot);
    this.showMenu = false;
    this.hasMetRequirements = !this._hasRequisiteProblem;
    this.courseLock = _4.get(this.genericSlot, 'isLockedByAdvisor', false);
    this.showAddToTermMenu = _4.get(this.genericSlot, 'shouldShowAddToTermMenu', false);
    if (this.showAddToTermMenu && this.isMovePreventedByMarkAsMet()) {
      this.showAddToTermMenu = false;
    }
    this.showRemoveFromTermMenu = this.isPlanned && !this.genericSlot.isLockedByStatus;
    this.showFlagCourseMenu = this.isPlanned && this.isAdvisor;
    this.addToTermMenuItemText = this.menuItemText();
    this.isSlotInPlannedStatus = !_4.includes(
      ['REGISTERED', 'IN_PROGRESS', 'COMPLETED', 'WAITLISTED'],
      this.genericSlot.status
    );
  }

  get isCourseCompleted() {
    return _4.get(this.genericSlot, 'isCourseCompleted', false);
  }

  get isMinGradeMet() {
    return _4.get(this.genericSlot, 'isMinGradeMet', false);
  }

  $onInit() {
    this.initialize();
  }

  get lockTooltipText() {
    if (!this.courseLock) {
      return '';
    }
    const termName = _4.get(_4.find(this.ngAPTermsQuery.result.plannedTerms, { id: this.genericSlot.termId }), 'name');
    return `This course is locked and will be unlocked at the end of the ${termName} term.`;
  }

  get _hasRequisiteProblem() {
    return _4.get(this.genericSlot, 'plannedCourse._hasRequisiteProblem', false);
  }

  $onChanges() {
    this.initialize();
  }

  openFlagPopup() {
    return this.routerHelper.findStateAndGo('.course_flag_dialog', { genericSlot: this.genericSlot });
  }

  showCourseDetails(event: any) {
    const courseNk = this.genericSlot.course.nk;
    const programSlotId = this.getSlot().id;
    const slotType = this.getSlot().slot_type;
    const termId = this.genericSlot.termId;
    const title = this.genericSlot.course._title;

    const courseDetails = {
      courseNk,
      programSlotId,
      slotType,
      termId,
      title
    };
    return this.routerHelper.findStateAndGo('.course_details', { courseDetails });
  }

  getSlot() {
    if (_4.isEmpty(this.genericSlot.programSlot)) {
      return {
        id: null,
        slot_type: null
      };
    }
    return {
      id: this.genericSlot.programSlot['id'] || null,
      slot_type: this.genericSlot.programSlot['slot_type']
    };
  }

  removeCourse() {
    if (!this.isPlanned) {
      return;
    }

    this.removeCourseFromTermCommand(this.genericSlot.programSlot, this.genericSlot.plannedCourse).then(() => {
      const termTitle = _4.get(
        _4.find(this.ngAPTermsQuery.result.plannedTerms, { id: this.genericSlot.termId }),
        'name'
      );
      this.phxAxTitleAnnouncerDelegate.announceChange(
        `${_4.get(this.genericSlot, 'plannedCourse._course.title')} was removed from ${termTitle}`
      );
    });
  }

  onMouseHover(event: any) {
    this.showMenu = true;
    const showLockOption = !_4.isEmpty(this.genericSlot.plannedCourse);
    if (!showLockOption) {
      return;
    }
    this.showLockCourseOption = this.showMenu && !this.courseLock && this.isSlotInPlannedStatus && this.isAdvisor;
    this.showUnlockCourseOption = this.showMenu && this.courseLock && this.isSlotInPlannedStatus && this.isAdvisor;
  }

  get shouldReplanCourse() {
    return this.genericSlot.isCourseCompleted && !this.genericSlot.isMinGradeMet;
  }

  moveToTerm(term: ITermEntry) {
    if (this.isPlanned && !this.shouldReplanCourse) {
      this.removeCourseFromTermAndAddCourseCommand(
        this.genericSlot.plannedCourse,
        this.genericSlot.course,
        this.genericSlot.programSlot || null,
        this.genericSlot.termId,
        term.id
      ).then((response: any) => {
        this.phxAxTitleAnnouncerDelegate.announceChange(
          `course ${this.genericSlot.course.title} was moved to ${term.name}`
        );
      });
    } else {
      const slot = this.getSlot();
      const is_devreq = this.genericSlot.programSlot.is_devreq || false;

      this.saveCourseToTermCommand(this.genericSlot.course.id, term.id, slot, is_devreq).then((response: any) => {
        this.ngComponentCommunicatorService.publish('CLOSE_LARGE_COURSE_GROUP_PANEL');
        if (_4.isNumber(this.genericSlot.programSlot.id)) {
          this.ngAPComponentStateModel.courseGroupClose(this.genericSlot.programSlot.id);
        }
        this.phxAxTitleAnnouncerDelegate.announceChange(`${this.genericSlot.title} added to ${term.name}`);
      });
    }
  }

  openLockUnlockDialog(mode: string) {
    return this.routerHelper.findStateAndGo('.lock_course', {
      genericSlot: this.genericSlot,
      type: mode
    });
  }
  menuOpened(event: CustomEvent) {
    this.isOpened = event.detail.value;
  }
}

class CourseTileMenu implements ng.IComponentOptions {
  public bindings: any;
  public controller: any;
  public templateUrl: string;

  constructor() {
    this.bindings = {
      genericSlot: '<',
      slot: '<'
    };
    this.controller = CourseTileMenuController;
    this.templateUrl = 'capability-ng-academic-planner/src/global/components/course_tile_menu/course_tile_menu.html';
  }
}

angular.module('capability-ng-academic-planner').component('courseTileMenu', new CourseTileMenu());



// WEBPACK FOOTER //
// ./app/vendor/capability-ng-academic-planner/src/global/components/course_tile_menu/course_tile_menu.ts